import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import {
  ChevronDown,
  ClipboardList,
  CreditCard,
  FilePlus,
  FileText,
  FolderOpen,
  HelpCircle,
  Info,
  LogIn,
  LogOut,
  Mail,
  Menu,
  MessageSquare,
  Newspaper,
  SlidersHorizontal,
  User,
  X,
} from 'lucide-react'
import { logout } from '../actions/userActions'
import { getUserTemplates } from '../actions/templateActions'
import BackdropProgress from './BackdropProgress'
import { GET_USER_TEMPLATES_RESET } from '../constants/templateConstants'
import { REFRESH_CREATE_SCREEN_REQUEST } from '../constants/createConstants'
import { OPEN_CREATE_PCR_DIALOG_RESET } from '../constants/userConstants'
import {
  RESET_GENERATED_PCR,
  RESET_APPLY_TEMPLATE,
  RESET_CONCISE_REPORT,
  RESET_REPORT_FEEDBACK,
  ENCODE_RESET,
} from '../constants/encodeConstants'
import './Header.css'

const NO_TEMPLATE = 'No template'

// id values are the contract with CreateScreen — 1/2/3 are persisted to
// localStorage as `lastReportTypeId` and passed in navigation state.
const REPORT_TYPES = [
  { id: 1, label: 'SOAP' },
  { id: 2, label: 'CHART' },
  { id: 3, label: 'DCHART' },
]

// Destinations that work signed in or out, so the bar never reshuffles under
// the user when their auth state changes. Everything else reachable on desktop
// lives in one of the two dropdowns — nothing is sheet-only, or it would be
// unreachable above 60rem where the menu button is hidden.
const CENTRE_LINKS = [
  { to: '/template', label: 'Templates' },
  { to: '/protocol', label: 'Protocol' },
]

const WORK_LINKS = [
  { to: '/template', label: 'Templates', Icon: FileText, auth: false },
  { to: '/documents', label: 'Documents', Icon: FolderOpen, auth: true },
  { to: '/report-config', label: 'Report configuration', Icon: SlidersHorizontal, auth: true },
  { to: '/protocol', label: 'Protocol', Icon: ClipboardList, auth: false },
  { to: '/feedback', label: 'Feedback', Icon: MessageSquare, auth: true },
]

const LEARN_LINKS = [
  { to: '/howto', label: 'How it works', Icon: HelpCircle },
  { to: '/faq', label: 'FAQ', Icon: HelpCircle },
  { to: '/blog', label: 'Blog', Icon: Newspaper },
  { to: '/about', label: 'About', Icon: Info },
  { to: '/contact', label: 'Contact', Icon: Mail },
]

function Header() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()

  const [reportTypeId, setReportTypeId] = useState(0)
  const [selectedTemplate, setSelectedTemplate] = useState(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [sheetOpen, setSheetOpen] = useState(false)
  // Only one dropdown may be open at a time: 'help' | 'account' | null.
  const [openMenu, setOpenMenu] = useState(null)

  const dialogRef = useRef(null)
  const sheetRef = useRef(null)
  const triggerRefs = useRef({})

  const { userInfo } = useSelector((state) => state.userLogin)
  const { userTemplatesLoading, userTemplates } = useSelector(
    (state) => state.userTemplateReducer
  )
  const { openDialogRequest } = useSelector((state) => state.openPCRCreateDialogReducer)

  const templates = userTemplates && userTemplates.templates ? userTemplates.templates : []

  const openCreateDialog = useCallback(() => {
    if (userTemplates == null) {
      dispatch(getUserTemplates(userInfo ? userInfo.id : -1))
    }
    setSelectedTemplate(null)
    // Only overwrite when a valid type was remembered — matches the original.
    const stored = Number(localStorage.getItem('lastReportTypeId') || 0)
    if (stored > 0 && stored <= REPORT_TYPES.length) {
      setReportTypeId(stored)
    }
    setSheetOpen(false)
    setOpenMenu(null)
    setDialogOpen(true)
  }, [dispatch, userInfo, userTemplates])

  // The landing page and other screens open this dialog by dispatching
  // OPEN_CREATE_PCR_DIALOG; the header owns the dialog itself.
  useEffect(() => {
    if (!openDialogRequest) return
    openCreateDialog()
    dispatch({ type: OPEN_CREATE_PCR_DIALOG_RESET })
  }, [openDialogRequest, openCreateDialog, dispatch])

  // Native <dialog> gives focus trapping, Esc-to-close and ::backdrop for free.
  useEffect(() => {
    const node = dialogRef.current
    if (!node) return
    if (dialogOpen && !node.open) node.showModal()
    if (!dialogOpen && node.open) node.close()
  }, [dialogOpen])

  useEffect(() => {
    const node = sheetRef.current
    if (!node) return
    if (sheetOpen && !node.open) node.showModal()
    if (!sheetOpen && node.open) node.close()
  }, [sheetOpen])

  // Dropdowns close on outside pointer-down and on Escape, returning focus to
  // the trigger so keyboard users aren't dropped at the page top. Clicking the
  // other trigger falls through to its own handler, which switches menus.
  useEffect(() => {
    if (!openMenu) return
    const onPointerDown = (event) => {
      if (event.target.closest && event.target.closest('.hd__has-menu')) return
      setOpenMenu(null)
    }
    const onKeyDown = (event) => {
      if (event.key !== 'Escape') return
      const trigger = triggerRefs.current[openMenu]
      setOpenMenu(null)
      if (trigger) trigger.focus()
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [openMenu])

  // Any route change closes the transient surfaces.
  useEffect(() => {
    setSheetOpen(false)
    setOpenMenu(null)
  }, [location.pathname])

  const logoutHandler = () => {
    setSelectedTemplate(null)
    setReportTypeId(0)
    dispatch(logout())
    dispatch({ type: GET_USER_TEMPLATES_RESET })
    setSheetOpen(false)
    setOpenMenu(null)
    navigate('/', { replace: true })
  }

  const handleCreate = () => {
    setDialogOpen(false)

    // Reset all Redux states before navigating
    dispatch({ type: RESET_GENERATED_PCR })
    dispatch({ type: RESET_APPLY_TEMPLATE })
    dispatch({ type: RESET_CONCISE_REPORT })
    dispatch({ type: RESET_REPORT_FEEDBACK })
    dispatch({ type: ENCODE_RESET })
    dispatch({ type: REFRESH_CREATE_SCREEN_REQUEST })

    navigate('/create', {
      state: {
        report: reportTypeId,
        template: selectedTemplate ? selectedTemplate.id : -1,
      },
    })
  }

  const handleTemplateChange = (event) => {
    const value = event.target.value
    const found = templates.find((entry) => String(entry.id) === value)
    setSelectedTemplate(found || null)
  }

  const isCurrent = (to) => (location.pathname === to ? 'page' : undefined)

  const renderSheetLink = ({ to, label, Icon }) => (
    <Link key={to} className="hd__menu-item" to={to} aria-current={isCurrent(to)}>
      <Icon size={17} aria-hidden="true" />
      {label}
    </Link>
  )

  return (
    <>
      <header className="hd">
        <div className="hd__inner">
          <Link className="hd__brand" to="/">
            PCRAssist.com
          </Link>

          <nav className="hd__center" aria-label="Main">
            {CENTRE_LINKS.map((item) => (
              <Link
                key={item.to}
                className="hd__link"
                to={item.to}
                aria-current={isCurrent(item.to)}
              >
                {item.label}
              </Link>
            ))}

            <div className="hd__has-menu">
              <button
                type="button"
                ref={(el) => {
                  triggerRefs.current.help = el
                }}
                className="hd__link"
                aria-expanded={openMenu === 'help'}
                aria-haspopup="menu"
                onClick={() => setOpenMenu((current) => (current === 'help' ? null : 'help'))}
              >
                Help
                <ChevronDown className="hd__caret" size={15} aria-hidden="true" />
              </button>

              {openMenu === 'help' && (
                <div className="hd__menu hd__menu--start" role="menu">
                  {LEARN_LINKS.map(({ to, label, Icon }) => (
                    <Link
                      key={to}
                      className="hd__menu-item"
                      role="menuitem"
                      to={to}
                      aria-current={isCurrent(to)}
                    >
                      <Icon size={17} aria-hidden="true" />
                      {label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </nav>

          <div className="hd__right">
            {userInfo ? (
              <div className="hd__account hd__has-menu">
                <button
                  type="button"
                  ref={(el) => {
                    triggerRefs.current.account = el
                  }}
                  className="hd__btn hd__btn--quiet"
                  aria-expanded={openMenu === 'account'}
                  aria-haspopup="menu"
                  onClick={() =>
                    setOpenMenu((current) => (current === 'account' ? null : 'account'))
                  }
                >
                  <User size={16} aria-hidden="true" />
                  <span className="hd__account-name">{userInfo.firstName}</span>
                  <ChevronDown className="hd__caret" size={15} aria-hidden="true" />
                </button>

                {openMenu === 'account' && (
                  <div className="hd__menu" role="menu">
                    <span className="hd__menu-label">Your work</span>
                    {WORK_LINKS.filter((item) => item.auth).map(({ to, label, Icon }) => (
                      <Link key={to} className="hd__menu-item" role="menuitem" to={to}>
                        <Icon size={17} aria-hidden="true" />
                        {label}
                      </Link>
                    ))}

                    <hr className="hd__menu-rule" />

                    <span className="hd__menu-label">Account</span>
                    <Link className="hd__menu-item" role="menuitem" to="/profile">
                      <User size={17} aria-hidden="true" />
                      Profile
                    </Link>
                    <Link className="hd__menu-item" role="menuitem" to="/credits">
                      <CreditCard size={17} aria-hidden="true" />
                      Credits
                    </Link>
                    {userInfo.isAdmin && (
                      <Link className="hd__menu-item" role="menuitem" to="/subscription">
                        <CreditCard size={17} aria-hidden="true" />
                        Subscription
                      </Link>
                    )}
                    <button
                      type="button"
                      className="hd__menu-item"
                      role="menuitem"
                      onClick={logoutHandler}
                    >
                      <LogOut size={17} aria-hidden="true" />
                      Sign out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link className="hd__btn hd__btn--quiet hd__account" to="/login">
                <LogIn size={16} aria-hidden="true" />
                Sign in
              </Link>
            )}

            <button
              type="button"
              className="hd__btn hd__btn--primary"
              onClick={openCreateDialog}
              aria-label="New report"
            >
              <FilePlus size={16} aria-hidden="true" />
              <span className="hd__cta-label">New report</span>
            </button>

            <button
              type="button"
              className="hd__btn hd__btn--quiet hd__iconbtn hd__menubtn"
              onClick={() => setSheetOpen(true)}
              aria-label="Open menu"
            >
              <Menu size={20} aria-hidden="true" />
            </button>
          </div>
        </div>
      </header>

      {/* ---- mobile sheet ------------------------------------------- */}
      <dialog
        ref={sheetRef}
        className="hd__sheet"
        onClose={() => setSheetOpen(false)}
        aria-label="Menu"
      >
        <div className="hd__sheet-inner">
          <div className="hd__sheet-bar">
            <span className="hd__brand" aria-hidden="true">
              PCRAssist.com
            </span>
            <button
              type="button"
              className="hd__btn hd__btn--quiet hd__iconbtn"
              onClick={() => setSheetOpen(false)}
              aria-label="Close menu"
            >
              <X size={20} aria-hidden="true" />
            </button>
          </div>

          <div className="hd__sheet-body">
            <button type="button" className="hd__btn hd__btn--primary" onClick={openCreateDialog}>
              <FilePlus size={16} aria-hidden="true" />
              New report
            </button>

            <div className="hd__group">
              <span className="hd__group-label">Your work</span>
              {WORK_LINKS.filter((item) => !item.auth || userInfo).map(renderSheetLink)}
            </div>

            <div className="hd__group">
              <span className="hd__group-label">Learn</span>
              {LEARN_LINKS.map(renderSheetLink)}
            </div>

            <div className="hd__group">
              <span className="hd__group-label">Account</span>
              {userInfo ? (
                <>
                  {renderSheetLink({ to: '/profile', label: 'Profile', Icon: User })}
                  {renderSheetLink({ to: '/credits', label: 'Credits', Icon: CreditCard })}
                  {userInfo.isAdmin &&
                    renderSheetLink({
                      to: '/subscription',
                      label: 'Subscription',
                      Icon: CreditCard,
                    })}
                  <button type="button" className="hd__menu-item" onClick={logoutHandler}>
                    <LogOut size={17} aria-hidden="true" />
                    Sign out
                  </button>
                </>
              ) : (
                <>
                  {renderSheetLink({ to: '/login', label: 'Sign in', Icon: LogIn })}
                  {renderSheetLink({ to: '/register', label: 'Create an account', Icon: User })}
                </>
              )}
            </div>
          </div>
        </div>
      </dialog>

      {/* ---- create dialog ------------------------------------------ */}
      <dialog
        ref={dialogRef}
        className="hd__dialog"
        onClose={() => setDialogOpen(false)}
        aria-labelledby="hd-dialog-title"
      >
        {userTemplatesLoading ? <BackdropProgress /> : null}

        <div className="hd__dialog-head">
          <h2 id="hd-dialog-title">Start a report</h2>
        </div>

        <div className="hd__dialog-body">
          <fieldset className="hd__fieldset">
            <legend className="hd__legend">Report type</legend>
            <div className="hd__seg">
              {REPORT_TYPES.map((type) => (
                <label className="hd__seg-opt" key={type.id}>
                  <input
                    type="radio"
                    name="hd-report-type"
                    value={type.id}
                    checked={reportTypeId === type.id}
                    onChange={() => setReportTypeId(type.id)}
                  />
                  <span>{type.label}</span>
                </label>
              ))}
            </div>
          </fieldset>

          {templates.length > 0 ? (
            <div className="hd__field">
              <label className="hd__label" htmlFor="hd-template">
                Template <span className="hd__note">(optional)</span>
              </label>
              <select
                id="hd-template"
                className="hd__select"
                value={selectedTemplate ? String(selectedTemplate.id) : ''}
                onChange={handleTemplateChange}
              >
                <option value="">{NO_TEMPLATE}</option>
                {templates.map((entry) => (
                  <option key={entry.id} value={String(entry.id)}>
                    {entry.name}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <p className="hd__note">
              No templates saved yet — the report will use the standard format.
            </p>
          )}
        </div>

        <div className="hd__dialog-foot">
          <button
            type="button"
            className="hd__btn hd__btn--quiet hd__dialog-foot-spacer"
            onClick={() => setDialogOpen(false)}
          >
            Cancel
          </button>
          <button
            type="button"
            className="hd__btn hd__btn--primary"
            onClick={handleCreate}
            disabled={reportTypeId === 0}
          >
            Start
          </button>
        </div>
      </dialog>
    </>
  )
}

export default Header
